import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { format, formatDistanceToNow, isValid } from "date-fns";
import { CornerDownRight, MapPinned, ScanEye, Send, View } from "lucide-react";
import { UserAgentDeviceBadge } from "../tracking/UserAgentDeviceBadge";
import { getMessageTracking } from "../api/InboxApi";
import { SmallLoading } from "../main/Loading";
import { getBaseUrl } from "../api/api";

const MessageTracking = ({ trackingId, hasGmail, hasOutlook }) => {
  const { t } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ["message-tracking", trackingId],
    queryFn: () => getMessageTracking(trackingId),
    enabled: !!trackingId,
    staleTime: 60 * 1000, // cache 60s
  });

  if (isLoading)
    return (
      <div className="w-full justify-items-center">
        <SmallLoading />
      </div>
    );

  const trackingResult = fromJS(data?.length > 0 ? data[0] : {});
  const geoLocations = trackingResult.get("geoLocations") || fromJS([]);

  const getLocationInfo = (viewTrackingItem: any) => {
    const city = viewTrackingItem.getIn(["ipLocation", "city"]) || "";
    const region = viewTrackingItem.getIn(["ipLocation", "region"]) || "";
    if (city == "") {
      return t("Unknown location");
    }
    return `${city}, ${region}`;
  };

  const convertDate = (date) => {
    if (!date) return null;
    const utcDate = date.replace(" ", "T") + "Z";
    return new Date(utcDate);
  };

  const renderIconEmail = () => {
    if (hasGmail) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/google.png`}
          alt="Google"
        />
      );
    }
    if (hasOutlook) {
      return (
        <img
          className="w-4 h-4 object-contain"
          src={`${getBaseUrl()}/static/images/microsoft.png`}
          alt="Microsoft"
        />
      );
    }
    return null;
  };

  const renderLastView = () => {
    let viewInfo;
    let title;
    const hasViews = geoLocations.size > 0;
    if (hasViews) {
      viewInfo = "1 min";
      title = "1 min";
      const updatedAt = geoLocations.getIn([geoLocations.size - 1, "updatedAt"]);
      const date = convertDate(updatedAt);
      if (isValid(date)) {
        viewInfo = formatDistanceToNow(date);
        title = format(date, "PPpp");
      }
    }
    const lastGeoLocation = geoLocations.last() || fromJS({});
    const ua = lastGeoLocation.get("userAgent") as string | undefined;
    return (
      <>
        <div title={title} className="text-base font-semibold">
          {viewInfo ? `${viewInfo} ago` : "-"}
        </div>
        <div className="flex items-start gap-2 min-w-0">
          <div className="flex flex-col items-center shrink-0 gap-0.5 pt-0.5">
            <CornerDownRight className="h-4 w-4 text-gray-500" />
            <UserAgentDeviceBadge userAgent={ua} iconClassName="w-3 h-3" />
          </div>
          <div className="text-gray-500 text-sm min-w-0 pt-0.5">
            <div className="truncate">{getLocationInfo(lastGeoLocation)}</div>
          </div>
        </div>
      </>
    );
  };

  const renderSent = () => {
    let viewInfo;
    let title;
    if (trackingResult.get("createdAt")) {
      viewInfo = "1 min";
      title = "1 min";
      const date = convertDate(trackingResult.get("createdAt"));
      if (isValid(date)) {
        viewInfo = formatDistanceToNow(date);
        title = format(date, "PPpp");
      }
    }
    return (
      <>
        <div title={title} className="text-base font-semibold">
          {viewInfo ? `${viewInfo} ago` : "-"}
        </div>
        <div className="flex items-center gap-2">
          {renderIconEmail()}
          <div className="text-gray-500 text-sm">{trackingResult.get("emailAddress")}</div>
        </div>
      </>
    );
  };

  return (
    <div className="grid grid-cols-4 gap-4">
      <div className="h-24 p-2 rounded-lg border border-gray-200 bg-gray-50 grid grid-flow-col grid-rows-3 gap-1">
        <div className="flex items-center gap-2">
          <ScanEye className="w-6 h-6 text-gray-500" />
          <div className="text-gray-500">{t("Views")}</div>
        </div>
        <div></div>
        <div className="text-base font-semibold">{trackingResult.get("readCount") || 0}</div>
      </div>
      <div className="h-24 p-2 rounded-lg border border-gray-200 bg-gray-50 grid grid-flow-col grid-rows-3 gap-1">
        <div className="flex items-center gap-2">
          <MapPinned className="w-6 h-6 text-gray-500" />
          <div className="text-gray-500">{t("Locations")}</div>
        </div>
        <div></div>
        <div className="text-base font-semibold">{geoLocations.size}</div>
      </div>
      <div className="h-24 p-2 rounded-lg border border-gray-200 bg-gray-50 grid grid-flow-col grid-rows-3 gap-1">
        <div className="flex items-center gap-2">
          <View className="w-6 h-6 text-gray-500" />
          <div className="text-gray-500">{t("Last view")}</div>
        </div>
        {renderLastView()}
      </div>
      <div className="h-24 p-2 rounded-lg border border-gray-200 bg-gray-50 grid grid-flow-col grid-rows-3 gap-1">
        <div className="flex items-center gap-2">
          <Send className="w-6 h-6 text-gray-500" />
          <div className="text-gray-500">{t("Sent")}</div>
        </div>
        {renderSent()}
      </div>
    </div>
  );
};

export default MessageTracking;
